import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "SolidLiquidSeparation",
    relatedTopic: "screening",
    hero: {
      categoryBadge: "Screening & Separation",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Mesh & Wedge Wire for Solid-Liquid Separation",
      lead: "Engineered continuous-slot wedge wire screens and heavy-duty woven mesh designed for slurry dewatering, solids recovery, and industrial liquid clarification without blinding under continuous process flow.",
      secondaryCta: {
        label: "Screening & Separation Hub",
        to: "/ScreeningAndSeparationHubPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Stainless steel wedge wire dewatering screen panel showing precision continuous slot profiles",
      },
      cardNote: "Self-relieving V-wire & non-blinding geometry",
      cardLink: {
        label: "Technical Consultation",
        to: "/ContactPage",
      },
      specs: [
        { label: "Slot Range", value: "20 µm – 3,000 µm" },
        { label: "Slot Accuracy", value: "±5 µm (from 20 µm)" },
        { label: "Wire Profile", value: "Non-Clogging V-Wire" },
        { label: "Alloy Grades", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "High-Efficiency Continuous Separation for High-Solids Slurries and Process Liquids",
      paragraphs: [
        "Industrial processing circuits, mineral refining, chemical synthesis, and industrial wastewater treatment generate demanding solid-liquid suspensions that require rapid phase separation. Traditional filter media frequently encounter blinding or mechanical failure when confronted with dense slurries, fibrous debris, and abrasive mineral particles under continuous flow.",
        "To overcome these operational challenges, MutualFilter supplies precision-welded continuous-slot wedge wire screens and heavy-duty stainless steel woven wire cloth. The self-relieving V-wire profile provides two-point particle contact at the slot entry, ensuring that near-size solids release immediately and prevent blinding across extended continuous duty.",
        "Fabricated from passivated AISI 304 and 316L stainless steel, every screen panel, curved sieve bend, and rotary basket maintains rigid mechanical geometry and strict slot tolerances, supporting mechanical scraper operations, vibratory acceleration, and automated backwash cycles.",
      ],
      keyPoints: [
        "Non-clogging V-wire profile provides two-point particle contact to discharge solids rapidly without particulate blinding",
        "Continuous slot openings calibrated from 20 µm to 3,000 µm maintain strict dimensional consistency across the entire active screen face",
        "Heavy load-bearing support rods withstand differential pressure spikes, mechanical scraper forces, and centrifuge shear",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Precision stainless steel wedge wire dewatering screen panel for solid liquid separation",
      },
    },
    specTable: {
      heading: "Media Configurations for Solid-Liquid Separation Operations",
      intro: "Representative metallic filter media configurations tailored for slurry dewatering, solids recovery, and mechanical separation machinery.",
      columns: [
        { key: "duty", label: "Process Duty", align: "left" },
        { key: "mediaType", label: "Media Construction", align: "left" },
        { key: "aperture", label: "Slot / Aperture Range", align: "right" },
        { key: "alloys", label: "Standard Alloys", align: "right" },
        { key: "cleaning", label: "Cake Release Mechanism", align: "right" },
      ],
      rows: [
        {
          duty: "Static Slurry Dewatering",
          mediaType: "Curved sieve bend wedge wire screen",
          aperture: "0.25 mm – 3.0 mm",
          alloys: "AISI 304 / 316L",
          cleaning: "Gravity cross-flow shear",
        },
        {
          duty: "Centrifugal Solids Recovery",
          mediaType: "Axial slot wedge wire basket cylinder",
          aperture: "50 µm – 1,000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Centrifugal discharge / scraper",
        },
        {
          duty: "Vibratory Fine Particle Dewatering",
          mediaType: "Heavy square weave wire cloth",
          aperture: "75 µm – 500 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Vibratory motion ejection",
        },
        {
          duty: "Screw Press Liquid Clarification",
          mediaType: "Reinforced circumferential slot tube",
          aperture: "100 µm – 2,000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Continuous screw flight wipe",
        },
      ],
      caption: "All slot dimensions, support rod pitches, and framing profiles are engineered to order according to process slurry characteristics.",
    },
    features: {
      heading: "Key Operational & Manufacturing Advantages",
      intro: "Our stainless steel solid-liquid separation media combine structural endurance, non-blinding profile geometry, and metallurgical integrity across continuous processing.",
      items: [
        {
          title: "Non-Clogging V-Wire Geometry",
          text: "Continuous-slot profile wires feature widening relief angles that ensure near-size particles touch only two contact points before discharging freely.",
        },
        {
          title: "Heavy Load-Bearing Support Structure",
          text: "High-yield resistance welds fuse surface profile wires directly to sturdy transverse support rods, sustaining heavy solids beds and mechanical vibration.",
        },
        {
          title: "Corrosion & Wear Resistance",
          text: "Precision-drawn AISI 304 and molybdenum-bearing 316L stainless steel alloys withstand abrasive slurry flows and corrosive chemical carrier fluids.",
        },
        {
          title: "Custom Framing & Edge Preparations",
          text: "Supplied with custom hook strips, reinforced flanges, backing channels, or precision-machined end rings for drop-in installation in OEM separation equipment.",
        },
      ],
    },
    related: {
      heading: "Related Separation Systems & Recommended Products",
      intro: "Explore complementary screening and separation applications alongside precision metallic media manufactured for demanding industrial processing.",
      cards: [
        {
          title: "Screening & Separation Hub",
          text: "Comprehensive engineering overview covering gas-liquid demisting, slurry dewatering, and precision powder classification.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Parent Application",
          linkLabel: "View Hub Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Screening and separation hub overview showing industrial separation media",
          },
        },
        {
          title: "Gas Liquid Separation",
          text: "Engineered knitted wire mesh demister pads and sintered laminates designed to capture entrained droplets down to 1 µm – 50 µm.",
          to: "/GasLiquidSeparationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Gas-Liquid",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
            alt: "Knitted wire mesh demister pad for gas liquid separation vessels",
          },
        },
        {
          title: "Powder Particle Screening",
          text: "Precision square weave wire mesh calibrated from 20 µm to 25 mm (1 to 635 mesh) for high-frequency vibratory classification.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Sibling Application",
          linkLabel: "Explore Powder Screening",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Precision woven wire mesh for powder and particle classification",
          },
        },
        {
          title: "Wedge Wire Screens",
          text: "Continuous-slot V-wire flat panels, curved sieve bends, and screen cylinders engineered for non-clogging solid-liquid dewatering.",
          to: "/WedgeWireScreenPage",
          badge: "Recommended Product",
          linkLabel: "Explore Wedge Wire",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Curved wedge wire sieve bend screen panel for solid liquid dewatering",
          },
        },
        {
          title: "Woven Wire Mesh",
          text: "High-precision square weave wire cloth from 1 to 635 mesh and Dutch weave filter cloth for liquid straining and solids recovery.",
          to: "/WovenWireMeshPage",
          badge: "Recommended Product",
          linkLabel: "Explore Woven Mesh",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Stainless steel woven wire mesh for separation and screening operations",
          },
        },
      ],
    },
    cta: {
      id: "solid-liquid-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Solid-Liquid Separation Screen Specification",
      lead: "Provide your required slot width or mesh rating, panel dimensions, alloy selection, and equipment mounting requirements for a technical evaluation and quote.",
      secondaryLabel: "Screening & Separation Hub",
      secondaryTo: "/ScreeningAndSeparationHubPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "SolidLiquidSeparation",
    relatedTopic: "screening",
    hero: {
      categoryBadge: "筛分与分离",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "固液分离与矿浆脱水精密不锈钢筛网",
      lead: "专为高浓度悬浮矿浆、工业废水与物料脱水打造的高强度连续缝隙筛网。采用自释形 V 型截面丝焊接构型，在耐受工况压差与物料冲刷的同时从根源消除孔隙堵塞。",
      secondaryCta: {
        label: "筛分与分离应用总览",
        to: "/ScreeningAndSeparationHubPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "展示高精度连续缝隙截面的不锈钢楔形丝脱水筛板",
      },
      cardNote: "自释形 V 型丝与防堵塞截面型线",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "缝隙开孔", value: "20 µm – 3,000 µm" },
        { label: "缝隙公差", value: "±5 µm（20 µm 起）" },
        { label: "型线几何", value: "自释形 V 型丝" },
        { label: "标准材质", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "针对高固含浆料与工艺废液的高效连续固液分离",
      paragraphs: [
        "矿物精选、化工合成、造纸以及工业废液处理工艺回路中，常年产生大流量高固含悬浮矿浆，亟需连续稳定的固液相分离。传统过滤介质在面对稠厚浆体、纤维交织物及高硬度磨蚀性矿粒时，极易发生孔隙嵌塞或因高压差造成结构变形损伤。",
        "为攻克严苛工况挑战，MutualFilter 提供全金属精密焊接连续缝隙楔形网及重载不锈钢编织丝网。独特的自释形 V 型截面丝在缝隙入口处提供微小两点接触，使接近缝宽的临界粒径颗粒在过缝后立即脱离，从几何构型上消除堵网隐患。",
        "选用酸洗钝化处理的 AISI 304 与 316L 不锈钢精工制造，无论是平面筛板、弧形筛还是转鼓离心筛筒，均具备优异的抗弯抗剪刚度与精密一致的缝隙公差，全面适应机械刮刷、高频振动及周期性反冲洗工况。",
      ],
      keyPoints: [
        "两点微接触自释形 V 型丝截面使截留固体快速释放，彻底消除堵网嵌塞隐患",
        "连续缝隙开孔精确覆盖 20 µm 至 3,000 µm，整板缝隙公差高度均一稳定",
        "重载焊接筋条骨架承受水锤脉冲、机械刮刀剪切与离心载荷，维持长期结构刚度",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "用于固液分离的精密不锈钢楔形丝脱水筛板",
      },
    },
    specTable: {
      heading: "固液分离与脱水常用筛网滤材配置参数",
      intro: "以下列出适用于静态脱水、离心转鼓、振动脱水以及压榨分离工况的典型金属介质配置。",
      columns: [
        { key: "duty", label: "分离工艺环节", align: "left" },
        { key: "mediaType", label: "核心构型与介质", align: "left" },
        { key: "aperture", label: "缝隙 / 孔径范围", align: "right" },
        { key: "alloys", label: "推荐合金材质", align: "right" },
        { key: "cleaning", label: "排渣脱饼方式", align: "right" },
      ],
      rows: [
        {
          duty: "静态重力矿浆脱水",
          mediaType: "弧形脱水筛板 (Sieve Bend)",
          aperture: "0.25 mm – 3.0 mm",
          alloys: "AISI 304 / 316L",
          cleaning: "重力横向错流剪切",
        },
        {
          duty: "离心机固相浓缩回收",
          mediaType: "轴向缝隙离心筛筒转鼓",
          aperture: "50 µm – 1,000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "离心抛料 / 旋转刮刀",
        },
        {
          duty: "细粒物料振动脱水",
          mediaType: "重载不锈钢方孔编织网",
          aperture: "75 µm – 500 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "高频振动抛掷排渣",
        },
        {
          duty: "螺旋压榨脱水澄清",
          mediaType: "内加固环向连续缝隙筛管",
          aperture: "100 µm – 2,000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "连续螺旋转子刮壁",
        },
      ],
      caption: "各项缝隙精度、支撑筋条间距及外框法兰形式均可根据现场物料工况定制加工。",
    },
    features: {
      heading: "关键运行与结构技术优势",
      intro: "我们的不锈钢固液分离介质兼具抗压耐磨刚性、防堵塞截面几何与可靠的耐腐蚀冶金性能，适应长周期连续作业。",
      items: [
        {
          title: "V型自洁防堵截面型线",
          text: "连续缝隙楔形丝具有向外扩展的侧边斜度，颗粒仅与迎水面两点接触即被截留或顺利通过，显著降低嵌塞堵孔风险。",
        },
        {
          title: "高抗拉承载与重载骨架",
          text: "精密电阻阻焊工艺将表面型线与重型支撑筋条牢固结合，能够承受高浓度固体床层重压与剧烈机械振动。",
        },
        {
          title: "优异耐磨蚀与化学稳定性",
          text: "精密拉拔 AISI 304 与含钼 316L 不锈钢材料，在磨蚀性矿浆流与腐蚀性化学载液中保持出色的耐冲蚀寿命。",
        },
        {
          title: "定制化边框与接口形式",
          text: "可定制折边钩条、加固法兰接口、承重背梁或高精度机加工端环，便捷适配各类主机厂分离设备。",
        },
      ],
    },
    related: {
      heading: "相关分离应用与推荐产品",
      intro: "浏览 MutualFilter 为工业流体与分离工程打造的互补方案及精密金属过滤产品。",
      cards: [
        {
          title: "筛分与分离总览",
          text: "涵盖气液除沫、矿浆脱水与精细粉体筛分的工程级金属分离介质全景技术方案。",
          to: "/ScreeningAndSeparationHubPage",
          badge: "父级应用分类",
          linkLabel: "查看总览方案",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "筛分与分离应用总览金属筛板展示",
          },
        },
        {
          title: "气液分离",
          text: "精工制造的针织丝网除沫器垫与烧结复合网，针对 1 µm 至 50 µm 雾滴消除气体夹带。",
          to: "/GasLiquidSeparationPage",
          badge: "同级应用",
          linkLabel: "探索气液分离",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
            alt: "工业气液分离容器用不锈钢针织除沫网垫",
          },
        },
        {
          title: "粉体与颗粒筛分",
          text: "精密方孔不锈钢编织丝网，孔径覆盖 20 µm 至 25 mm（1 至 635 目），适配高频振动筛。",
          to: "/PowderAndParticleScreeningPage",
          badge: "同级应用",
          linkLabel: "探索颗粒筛分",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "用于粉体筛分的精密方孔编织丝网",
          },
        },
        {
          title: "楔形丝筛网",
          text: "连续缝隙 V 型绕丝平板筛、弧形脱水筛与筛筒，专为高负荷固液分离与矿浆脱水设计。",
          to: "/WedgeWireScreenPage",
          badge: "推荐产品",
          linkLabel: "探索楔形丝网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "固液脱水用弧形楔形丝脱水筛板",
          },
        },
        {
          title: "不锈钢编织网",
          text: "符合 ISO 9044 与 ASTM E2016 规范的方孔编织丝网与密纹网，覆盖 1 至 635 目精细范围。",
          to: "/WovenWireMeshPage",
          badge: "推荐产品",
          linkLabel: "探索编织网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "分离设备用精密不锈钢编织丝网",
          },
        },
      ],
    },
    cta: {
      id: "solid-liquid-quote",
      eyebrow: "定制方案咨询",
      heading: "获取固液分离筛网与滤材技术报价",
      lead: "请提供您的目标缝隙/孔径、外形尺寸、材质牌号及设备安装要求，我们的工程团队将为您提供针对性的选型评估与报价。",
      secondaryLabel: "返回筛分与分离总览",
      secondaryTo: "/ScreeningAndSeparationHubPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "SolidLiquidSeparation",
    relatedTopic: "screening",
    hero: {
      categoryBadge: "Tamizado y Separación",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mallas de Acero Inoxidable y Alambre en Cuña para Separación Sólido-Líquido",
      lead: "Cribas de alambre en cuña de ranura continua y telas metálicas pesadas para deshidratación de lodos, recuperación de sólidos y clarificación industrial sin riesgo de colmatación.",
      secondaryCta: {
        label: "Centro de Tamizado y Separación",
        to: "/ScreeningAndSeparationHubPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Panel de cribado de deshidratación en alambre en cuña de acero inoxidable con ranura continua",
      },
      cardNote: "Perfil en V anticolmatante y descarga rápida",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de Ranura", value: "20 µm – 3.000 µm" },
        { label: "Precisión Ranura", value: "±5 µm (desde 20 µm)" },
        { label: "Perfil de Alambre", value: "Alambre en V Anticolmatante" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Separación Continua de Alta Eficiencia para Lodos Concentrados y Líquidos Industriales",
      paragraphs: [
        "Los circuitos de procesamiento industrial, la refinación mineral y el tratamiento de aguas residuales generan suspensiones sólido-líquido complejas que requieren una separación de fases continua. Los medios filtrantes convencionales suelen sufrir atascos o deformaciones bajo la acción de lodos densos y sólidos abrasivos.",
        "Para responder a estas demandas, MutualFilter suministra cribas de alambre en cuña soldadas de ranura continua y mallas de alambre tejido de alta resistencia. El perfil en V autolimpiante garantiza un contacto en solo dos puntos en la entrada de la ranura, facilitando la liberación inmediata de los sólidos retenidos.",
        "Fabricados en acero inoxidable pasivado AISI 304 y 316L, los paneles planos, cribas curvas y cestas cilíndricas mantienen tolerancias estrictas y rigidez mecánica frente a rascadores, fuerzas vibratorias y ciclos de lavado continuo.",
      ],
      keyPoints: [
        "Perfil en V anticolmatante con contacto en dos puntos que descarga sólidos con rapidez y previene el bloqueo",
        "Ranuras continuas calibradas de 20 µm a 3.000 µm con estricta uniformidad dimensional en toda la superficie activa",
        "Varillas de soporte robustas para soportar picos de presión diferencial, rascadores mecánicos y fuerzas centrífugas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Panel de criba de alambre en cuña para separación sólido-líquido",
      },
    },
    specTable: {
      heading: "Configuraciones de Medios para Separación Sólido-Líquido",
      intro: "Medios metálicos representativos para deshidratación de suspensiones, recuperación de sólidos y maquinaria de separación.",
      columns: [
        { key: "duty", label: "Aplicación de Proceso", align: "left" },
        { key: "mediaType", label: "Tipo de Construcción", align: "left" },
        { key: "aperture", label: "Ranura / Abertura", align: "right" },
        { key: "alloys", label: "Aleaciones Típicas", align: "right" },
        { key: "cleaning", label: "Método de Descarga", align: "right" },
      ],
      rows: [
        {
          duty: "Deshidratación Estática de Lodos",
          mediaType: "Criba curva parabólica (Sieve Bend)",
          aperture: "0,25 mm – 3,0 mm",
          alloys: "AISI 304 / 316L",
          cleaning: "Cizallamiento por gravedad",
        },
        {
          duty: "Recuperación Centrífuga de Sólidos",
          mediaType: "Cilindro de cesta de ranura axial",
          aperture: "50 µm – 1.000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Descarga centrífuga / rascador",
        },
        {
          duty: "Deshidratación Fina Vibratoria",
          mediaType: "Malla tejida cuadrada pesada",
          aperture: "75 µm – 500 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Expulsión por vibración",
        },
        {
          duty: "Prensas de Tornillo y Clarificación",
          mediaType: "Tubo reforzado de ranura circunferencial",
          aperture: "100 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Rascado continuo por tornillo",
        },
      ],
      caption: "Todas las dimensiones y anchos de ranura se fabrican a medida según las propiedades del lodo a tratar.",
    },
    features: {
      heading: "Ventajas Operativas y de Fabricación",
      intro: "Nuestros medios de separación combinan solidez estructural, perfil anticolmatante y resistencia a la corrosión para servicio continuo.",
      items: [
        {
          title: "Geometría Anticolmatante en V",
          text: "Las ranuras de perfil continuo cuentan con apertura divergente que asegura solo dos puntos de contacto con las partículas antes de liberarlas.",
        },
        {
          title: "Estructura de Soporte Reforzada",
          text: "Soldaduras de alta resistencia unen los hilos superficiales a varillas transversales robustas para tolerar vibraciones y peso de lecho.",
        },
        {
          title: "Resistencia al Desgaste y a la Corrosión",
          text: "Acero inoxidable AISI 304 y 316L estirado con precisión que resiste suspensiones abrasivas y fluidos químicos agresivos.",
        },
        {
          title: "Marcos y Fijaciones Personalizadas",
          text: "Disponibles con pestañas de tensión, bridas reforzadas y perfiles de fijación para una rápida instalación en equipos existentes.",
        },
      ],
    },
    related: {
      heading: "Sistemas de Separación y Productos Relacionados",
      intro: "Descubra soluciones complementarias de tamizado y medios metálicos de precisión diseñados por MutualFilter.",
      cards: [
        {
          title: "Centro de Tamizado y Separación",
          text: "Visión integral de soluciones para desgasificación, deshidratación de lodos y clasificación de partículas.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Visión general de cribas y medios de separación",
          },
        },
        {
          title: "Separación Gas-Líquido",
          text: "Eliminadores de niebla en malla tejida y laminados sinterizados para capturar gotas de 1 µm a 50 µm.",
          to: "/GasLiquidSeparationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Gas-Líquido",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
            alt: "Separador de gotas de malla tejida de acero inoxidable",
          },
        },
        {
          title: "Tamizado de Polvos y Partículas",
          text: "Mallas de tejido cuadrado calibradas de 20 µm a 25 mm (1 a 635 mesh) para clasificación vibratoria.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Polvos y Partículas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Malla tejida de acero inoxidable para tamizado de partículas",
          },
        },
        {
          title: "Cribas de Alambre en Cuña",
          text: "Paneles planos, cribas parabólicas y cilindros de ranura continua anticolmatantes para deshidratación exigente.",
          to: "/WedgeWireScreenPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Alambre en Cuña",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Criba curva parabólica en alambre en cuña",
          },
        },
        {
          title: "Malla de Alambre Tejida",
          text: "Telas de malla cuadrada de precisión de 1 a 635 mesh y tejidos Dutch para retención de partículas sólidas.",
          to: "/WovenWireMeshPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Malla Tejida",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Malla de alambre de acero inoxidable para tamizado",
          },
        },
      ],
    },
    cta: {
      id: "solid-liquid-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Cribas de Separación Sólido-Líquido",
      lead: "Indique la ranura o abertura requerida, dimensiones, aleación y tipo de fijación mecánica para una evaluación técnica y cotización.",
      secondaryLabel: "Volver al Centro de Separación",
      secondaryTo: "/ScreeningAndSeparationHubPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "SolidLiquidSeparation",
    relatedTopic: "screening",
    hero: {
      categoryBadge: "Siebung & Separation",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahldrahtgewebe & Spaltsiebe für Fest-Flüssig-Trennung",
      lead: "Kontinuierliche Spaltsiebe und schwere Drahtgewebe für Schlammentwässerung, Feststoffrückgewinnung und industrielle Klärung ohne Verstopfungsrisiko im Dauerbetrieb.",
      secondaryCta: {
        label: "Übersicht Siebung & Separation",
        to: "/ScreeningAndSeparationHubPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Edelstahl-Spaltsiebplatte zur Entwässerung mit präzisen kontinuierlichen Schlitzprofilen",
      },
      cardNote: "Verstopfungsfreier V-Draht & Zweipunktkontakt",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Spaltweiten", value: "20 µm – 3.000 µm" },
        { label: "Schlitzgenauigkeit", value: "±5 µm (ab 20 µm)" },
        { label: "Drahtprofil", value: "Verstopfungsfreier V-Draht" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Hocheffiziente kontinuierliche Trennung für feststoffreiche Suspensionen und Prozessflüssigkeiten",
      paragraphs: [
        "Industrielle Aufbereitungskreisläufe, Mineralverarbeitung und Abwasserströme führen hochkonzentrierte Fest-Flüssig-Gemische, die eine kontinuierliche und sichere Phasentrennung verlangen. Herkömmliche Siebmedien neigen bei abrasiven Schlämmen und faserigen Bestandteilen rasch zu Verstopfungen oder Verformungen.",
        "Um diesen Belastungen standzuhalten, fertigt MutualFilter präzisionsgeschweißte Spaltsiebe mit kontinuierlichen Schlitzen und schwere Edelstahldrahtgewebe. Das selbstreinigende V-Drahtprofil bietet an der Schlitzöffnung lediglich einen Zweipunktkontakt, wodurch Feststoffe sofort abgeworfen werden.",
        "Hergestellt aus passiviertem Edelstahl AISI 304 und 316L gewährleisten Flachpaneele, Bogensiebe und zylindrische Siebkörbe exakte Spaltweiten und hohe Stabilität gegenüber mechanischen Abstreifern, Vibrationen und Gegenstromspülungen.",
      ],
      keyPoints: [
        "Selbstreinigendes V-Drahtprofil mit Zweipunktkontakt für raschen Feststoffaustrag ohne Verkeilung",
        "Kontinuierliche Spaltweiten von 20 µm bis 3.000 µm mit engen Fertigungstoleranzen über die gesamte Siebfläche",
        "Stabile Trägerprofile widerstehen Scherkräften, mechanischen Schabern und zyklischen Druckstößen",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Präzisions-Spaltsiebplatte aus Edelstahl für Fest-Flüssig-Trennung",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für Fest-Flüssig-Trennverfahren",
      intro: "Repräsentative metallische Siebmedien für Schlammentwässerung, Feststoffrückgewinnung und Trennmaschinen.",
      columns: [
        { key: "duty", label: "Verfahrensaufgabe", align: "left" },
        { key: "mediaType", label: "Siebkonstruktion", align: "left" },
        { key: "aperture", label: "Spaltweite / Masche", align: "right" },
        { key: "alloys", label: "Standard-Werkstoffe", align: "right" },
        { key: "cleaning", label: "Kuchenaustrag", align: "right" },
      ],
      rows: [
        {
          duty: "Statische Schlammentwässerung",
          mediaType: "Bogensieb-Spaltsieb (Sieve Bend)",
          aperture: "0,25 mm – 3,0 mm",
          alloys: "AISI 304 / 316L",
          cleaning: "Schwerkraft-Querstromschur",
        },
        {
          duty: "Zentrifugale Feststoffgewinnung",
          mediaType: "Axialspalt-Siebkorbzylinder",
          aperture: "50 µm – 1.000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Zentrifugalaustrag / Schaber",
        },
        {
          duty: "Feinteilige Vibrationsentwässerung",
          mediaType: "Schweres Quadratmaschengewebe",
          aperture: "75 µm – 500 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Vibrationsabwurf",
        },
        {
          duty: "Schneckenpressen & Klärung",
          mediaType: "Verstärktes Radialspalt-Rohr",
          aperture: "100 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Schneckenabstreifung",
        },
      ],
      caption: "Alle Spaltweiten, Trägerabstände und Randprofile werden kundenbezogen auf die jeweilige Suspension ausgelegt.",
    },
    features: {
      heading: "Betriebliche & Konstruktive Vorteile",
      intro: "Unsere Edelstahlmedien für die Fest-Flüssig-Trennung vereinen strukturelle Dauerfestigkeit, verstopfungsfreie Geometrie und Korrosionsbeständigkeit.",
      items: [
        {
          title: "Verstopfungsfreies V-Draht-Profil",
          text: "Konische Schlitzöffnungen stellen sicher, dass Partikel nur an zwei Punkten anliegen und danach ungehindert abgeführt werden.",
        },
        {
          title: "Hochbelastbare Trägerkonstruktion",
          text: "Widerstandsgeschweißte Verbindungen zwischen Profildraht und Trägerstäben fangen schwere Feststofflasten und Vibrationen ab.",
        },
        {
          title: "Verschleiß- & Korrosionsbeständigkeit",
          text: "Präzise gezogene Edelstähle AISI 304 und molybdänhaltiges 316L widerstehen abrasiven Suspensionen und aggressiven Trägerflüssigkeiten.",
        },
        {
          title: "Individuelle Rand- & Rahmeneinfassungen",
          text: "Lieferbar mit Spannkanten, Flanschen oder bearbeiteten Endringen zur direkten Integration in bestehende Maschinen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Trennanwendungen & Empfohlene Produkte",
      intro: "Erkunden Sie weitere Trennlösungen und präzise metallische Filtermedien von MutualFilter.",
      cards: [
        {
          title: "Übersicht Siebung & Separation",
          text: "Umfassende Lösungen für Tropfenabscheidung, Schlammentwässerung und präzise Pulverklassierung.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Übersicht Sieb- und Abscheidemedien aus Edelstahl",
          },
        },
        {
          title: "Gas-Flüssig-Trennung",
          text: "Drahtgestrick-Tropfenabscheider und Sinterverbunde zur Abscheidung von Nebeln von 1 µm bis 50 µm.",
          to: "/GasLiquidSeparationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Gas-Flüssig ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
            alt: "Edelstahl-Drahtgestrick-Tropfenabscheider für Abscheidekolonnen",
          },
        },
        {
          title: "Pulver- & Partikelsiebung",
          text: "Präzisions-Drahtgewebe von 20 µm bis 25 mm (1 bis 635 Mesh) für Vibrationssiebmaschinen.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Pulversiebung ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Edelstahl-Drahtgewebe für Partikelklassierung",
          },
        },
        {
          title: "Spaltsiebe",
          text: "Verstopfungsfreie V-Draht-Flachpaneele, Bogensiebe und Zylinderrohre für Schlammentwässerung.",
          to: "/WedgeWireScreenPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Spaltsiebe entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Bogensieb aus Edelstahl zur Fest-Flüssig-Entwässerung",
          },
        },
        {
          title: "Drahtgewebe",
          text: "Präzisions-Quadratmaschen nach ISO 9044 und Tressengewebe für Flüssigkeitssiebung und Partikelabscheidung.",
          to: "/WovenWireMeshPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Drahtgewebe entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Edelstahl-Drahtgewebe für Siebung",
          },
        },
      ],
    },
    cta: {
      id: "solid-liquid-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für Fest-Flüssig-Trennsiebe anfordern",
      lead: "Geben Sie die gewünschte Spaltweite, Abmessungen, Werkstoff und Anschlussart für eine fachliche Auslegung an.",
      secondaryLabel: "Zurück zur Übersicht",
      secondaryTo: "/ScreeningAndSeparationHubPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "SolidLiquidSeparation",
    relatedTopic: "screening",
    hero: {
      categoryBadge: "Criblage & Séparation",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toiles Inox et Grilles à Fil Profilé pour la Séparation Solide-Liquide",
      lead: "Grilles à fente continue en fil profilé et toiles métalliques lourdes conçues pour l'égouttage des boues, la récupération de solides et la clarification industrielle sans risque de colmatage.",
      secondaryCta: {
        label: "Pôle Criblage & Séparation",
        to: "/ScreeningAndSeparationHubPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Panneau d'égouttage à fil profilé en acier inoxydable avec fentes continues de précision",
      },
      cardNote: "Fil en V auto-décolmatant & contact en 2 points",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage de Fente", value: "20 µm – 3 000 µm" },
        { label: "Précision de Fente", value: "±5 µm (dès 20 µm)" },
        { label: "Profil de Fil", value: "Fil en V Anti-Colmatant" },
        { label: "Nuances d'Alliage", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Séparation Continue Haute Efficacité pour Suspensions Chargées et Liquides Industriels",
      paragraphs: [
        "Les procédés industriels, la transformation minérale et le traitement des effluents traitent des suspensions solide-liquide complexes exigeant une séparation de phases continue. Les toiles filtrantes traditionnelles s'obstruent facilement sous l'effet de boues denses et de particules abrasives.",
        "Pour surmonter ces contraintes, MutualFilter fabrique des grilles en fil profilé à fente continue soudées avec précision et des toiles tissées en acier inoxydable renforcées. Le profil en V auto-décolmatant assure un contact ponctuel en deux points à l'entrée de la fente, évitant ainsi le coincement des grains.",
        "Réalisés en acier inoxydable passivé AISI 304 et 316L, les panneaux plans, cribles courbes et paniers cylindriques conservent une ouverture calibrée et une grande rigidité face aux racleurs, vibrations et lavages sous pression.",
      ],
      keyPoints: [
        "Profil en V auto-décolmatant assurant un contact en deux points pour évacuer les solides sans risque de colmatage",
        "Fentes continues de 20 µm à 3 000 µm usinées avec une tolérance rigoureuse sur toute la surface active",
        "Barres de renfort soudées résistant aux coups de bélier, aux racleurs mécaniques et aux contraintes centrifuges",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Panneau filtrant en fil profilé inox pour séparation solide-liquide",
      },
    },
    specTable: {
      heading: "Configurations de Médias pour la Séparation Solide-Liquide",
      intro: "Médias métalliques types pour l'égouttage de boues, la récupération de matières et les équipements de séparation.",
      columns: [
        { key: "duty", label: "Application Procédé", align: "left" },
        { key: "mediaType", label: "Construction du Média", align: "left" },
        { key: "aperture", label: "Plage de Fente / Maille", align: "right" },
        { key: "alloys", label: "Alliages Standards", align: "right" },
        { key: "cleaning", label: "Mode d'Évacuation", align: "right" },
      ],
      rows: [
        {
          duty: "Égouttage Statique de Boues",
          mediaType: "Grille courbe parabolique (Sieve Bend)",
          aperture: "0,25 mm – 3,0 mm",
          alloys: "AISI 304 / 316L",
          cleaning: "Cisaillement gravitaire",
        },
        {
          duty: "Récupération Centrifuge de Solides",
          mediaType: "Panier cylindrique à fente axiale",
          aperture: "50 µm – 1 000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Éjection centrifuge / racleur",
        },
        {
          duty: "Égouttage Vibrant de Particules Fines",
          mediaType: "Toile tissée à maille carrée lourde",
          aperture: "75 µm – 500 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Éjection par vibration",
        },
        {
          duty: "Presses à Vis & Clarification",
          mediaType: "Tube renforcé à fente circonférentielle",
          aperture: "100 µm – 2 000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Raclage continu par vis",
        },
      ],
      caption: "Dimensions, espacements des renforts et rives fabriqués sur mesure selon les propriétés du fluide.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos toiles et grilles combinent robustesse mécanique, profil anticolmatant et résistance à la corrosion pour un service continu.",
      items: [
        {
          title: "Profil Anticolmatant en Fil V",
          text: "L'ouverture divergente de la fente garantit que les particules n'ont que deux points de contact avant d'être dégagées sans obstruction.",
        },
        {
          title: "Armature Porteuse Renforcée",
          text: "Soudures haute tenue liant les fils profilés aux barres de support pour supporter les fortes charges de solides et les vibrations.",
        },
        {
          title: "Résistance à l'Usure et à la Corrosion",
          text: "Aciers inoxydables étirés AISI 304 et 316L au molybdène résistant aux liquides de procédé abrasifs et corrosifs.",
        },
        {
          title: "Encadrements et Fixations Spécifiques",
          text: "Livrables avec bandes de tension, brides de fixation ou anneaux usinés pour une adaptation immédiate aux machines.",
        },
      ],
    },
    related: {
      heading: "Systèmes de Séparation et Produits Associés",
      intro: "Découvrez nos solutions complémentaires de criblage et de filtration conçues par MutualFilter.",
      cards: [
        {
          title: "Pôle Criblage & Séparation",
          text: "Présentation générale des technologies de dévésiculage, d'égouttage de boues et de calibrage de poudres.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Présentation des médias métalliques de criblage et séparation",
          },
        },
        {
          title: "Séparation Gaz-Liquide",
          text: "Matelas dévésiculeurs en tricot métallique et composites frittés pour capter les gouttelettes de 1 µm à 50 µm.",
          to: "/GasLiquidSeparationPage",
          badge: "Application Associée",
          linkLabel: "Explorer Gaz-Liquide",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
            alt: "Matelas dévésiculeur en tricot métallique inox pour séparateur",
          },
        },
        {
          title: "Tamisage de Poudres & Particules",
          text: "Toiles métalliques de précision de 20 µm à 25 mm (1 à 635 mesh) pour tamiseurs vibrants.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Application Associée",
          linkLabel: "Explorer les Poudres",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Toile métallique pour le tamisage de poudres",
          },
        },
        {
          title: "Grilles à Fil Profilé",
          text: "Panneaux plans, grilles courbes et cylindres à fil profilé pour l'égouttage et la séparation solide-liquide.",
          to: "/WedgeWireScreenPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer le Fil Profilé",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Grille courbe en fil profilé pour séparation solide-liquide",
          },
        },
        {
          title: "Toiles Métalliques Tissées",
          text: "Toiles à mailles carrées conformes ISO 9044 et toiles hollandaises de 1 à 635 mesh pour la rétention solide.",
          to: "/WovenWireMeshPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Toiles Tissées",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Toile métallique inox à mailles carrées pour criblage",
          },
        },
      ],
    },
    cta: {
      id: "solid-liquid-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Grilles de Séparation Solide-Liquide",
      lead: "Précisez votre ouverture de fente, les dimensions hors tout, la nuance d'inox et le type de montage pour un chiffrage rapide.",
      secondaryLabel: "Retour au Pôle Séparation",
      secondaryTo: "/ScreeningAndSeparationHubPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "SolidLiquidSeparation",
    relatedTopic: "screening",
    hero: {
      categoryBadge: "Peneiramento & Separação",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Telas de Aço Inoxidável e Arame em Cunha para Separação Sólido-Líquido",
      lead: "Telas de arame em cunha com ranhura contínua e telas metálicas para desidratação de polpas, recuperação de sólidos e clarificação industrial sem risco de colmatação sob fluxo contínuo.",
      secondaryCta: {
        label: "Central de Peneiramento & Separação",
        to: "/ScreeningAndSeparationHubPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Painel de desidratação em arame perfilado em cunha com ranhuras contínuas de precisão",
      },
      cardNote: "Perfil em V antientupimento e dois pontos de contato",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de Abertura", value: "20 µm – 3.000 µm" },
        { label: "Precisão de Fenda", value: "±5 µm (a partir de 20 µm)" },
        { label: "Perfil de Arame", value: "Arame em V Antientupimento" },
        { label: "Ligas Padrão", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Separação Contínua de Alta Eficiência para Polpas de Alta Densidade e Fluidos Industriais",
      paragraphs: [
        "Circuitos de beneficiamento industrial, processamento químico e tratamento de efluentes geram suspensões sólido-líquido exigentes que necessitam de separação contínua de fases. Elementos filtrantes convencionais costumam sofrer entupimento severo ou deformações estruturais quando submetidos a polpas abrasivas e resíduos fibrosos.",
        "Para solucionar essas demandas, a MutualFilter fornece telas de arame em cunha soldadas com ranhura contínua e telas tecidas de aço inoxidável para serviço pesado. O perfil em V autolimpante garante contato em apenas dois pontos na abertura da fenda, permitindo que os sólidos sejam liberados imediatamente.",
        "Produzidos em aço inoxidável passivado AISI 304 e 316L, nossos painéis planos, telas curvas e cilindros mantêm precisão dimensional e rigidez mecânica sob raspadores mecânicos, vibrações severas e ciclos de retrolavagem.",
      ],
      keyPoints: [
        "Perfil em V autolimpante com contato em dois pontos para descarga rápida de sólidos e prevenção de colmatação",
        "Ranhuras contínuas calibradas de 20 µm a 3.000 µm com precisão dimensional consistente em toda a superfície ativa",
        "Barras de reforço soldadas projetadas para absorver pulsos de pressão, raspadores mecânicos e forças de cisalhamento",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
        alt: "Painel em arame em cunha para desidratação sólido-líquido",
      },
    },
    specTable: {
      heading: "Configurações de Telas para Separação Sólido-Líquido",
      intro: "Meios filtrantes metálicos representativos para desidratação de polpas, recuperação de sólidos e equipamentos de separação.",
      columns: [
        { key: "duty", label: "Etapa de Processo", align: "left" },
        { key: "mediaType", label: "Construção do Meio", align: "left" },
        { key: "aperture", label: "Ranhura / Abertura", align: "right" },
        { key: "alloys", label: "Ligas Metálicas", align: "right" },
        { key: "cleaning", label: "Mecanismo de Descarga", align: "right" },
      ],
      rows: [
        {
          duty: "Desidratação Estática de Polpa",
          mediaType: "Peneira parabólica curva (Sieve Bend)",
          aperture: "0,25 mm – 3,0 mm",
          alloys: "AISI 304 / 316L",
          cleaning: "Cisalhamento por gravidade",
        },
        {
          duty: "Recuperação Centrífuga de Sólidos",
          mediaType: "Cesto cilíndrico com ranhura axial",
          aperture: "50 µm – 1.000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Descarga centrífuga / raspador",
        },
        {
          duty: "Desidratação Fina Vibratória",
          mediaType: "Tela tecida de malha quadrada pesada",
          aperture: "75 µm – 500 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Ejeção por vibração",
        },
        {
          duty: "Prensas de Rosca & Clarificação",
          mediaType: "Tubo reforçado com fenda circunferencial",
          aperture: "100 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Raspagem contínua por rosca",
        },
      ],
      caption: "Todas as dimensões, aberturas de ranhura e reforços são fabricados sob medida conforme as características da suspensão.",
    },
    features: {
      heading: "Destaques Construtivos e Operacionais",
      intro: "Nossas telas metálicas combinam resistência mecânica, geometria anti-obstrução e excelente integridade metalúrgica para operação ininterrupta.",
      items: [
        {
          title: "Geometria em V Antientupimento",
          text: "As ranhuras contínuas com abertura divergente asseguram apenas dois pontos de contato com as partículas antes de sua liberação.",
        },
        {
          title: "Estrutura Portante Reforçada",
          text: "Soldas por resistência de alto rendimento fundem os arames de superfície às barras de suporte para resistir a altas cargas e vibração.",
        },
        {
          title: "Resistência ao Desgaste e Corrosão",
          text: "Ligas de aço inoxidável AISI 304 e 316L com molibdênio suportam polpas abrasivas e fluidos químicos industriais agressivos.",
        },
        {
          title: "Molduras e Fixações Customizadas",
          text: "Fornecidas com bordas de fixação, flanges reforçados ou anéis usinados para fácil montagem em máquinas de fabricantes originais.",
        },
      ],
    },
    related: {
      heading: "Sistemas de Separação e Produtos Relacionados",
      intro: "Conheça soluções integradas de peneiramento e meios metálicos de precisão fabricados pela MutualFilter.",
      cards: [
        {
          title: "Central de Peneiramento & Separação",
          text: "Soluções integradas cobrindo desaerossolização, desidratação de polpas e classificação precisa de pós.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Visão geral de peneiramento e separação com meios metálicos",
          },
        },
        {
          title: "Separação Gás-Líquido",
          text: "Eliminadores de névoa em malha tricotada e compósitos sinterizados para gotículas de 1 µm a 50 µm.",
          to: "/GasLiquidSeparationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Gás-Líquido",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
            alt: "Eliminador de névoa em malha de arame para separação gás-líquido",
          },
        },
        {
          title: "Peneiramento de Pós e Partículas",
          text: "Telas de malha quadrada calibradas de 20 µm a 25 mm (1 a 635 mesh) para classificação vibratória.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Peneiramento",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Tela tecida de aço inoxidável para classificação de partículas",
          },
        },
        {
          title: "Telas de Arame em Cunha",
          text: "Painéis planos, telas parabólicas e cilindros com ranhura contínua antientupimento para desidratação contínua.",
          to: "/WedgeWireScreenPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Arame em Cunha",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Painel de peneira curva em cunha para desidratação sólido-líquido",
          },
        },
        {
          title: "Telas de Arame Tecidas",
          text: "Telas de malha quadrada de precisão de 1 a 635 mesh e tramas holandesas para separação e retenção de sólidos.",
          to: "/WovenWireMeshPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Telas Tecidas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Tela de arame de aço inoxidável para separação e peneiramento",
          },
        },
      ],
    },
    cta: {
      id: "solid-liquid-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite uma Cotação para Telas de Separação Sólido-Líquido",
      lead: "Informe a abertura de ranhura desejada, dimensões totais, liga de aço inoxidável e fixação mecânica para análise técnica e orçamento.",
      secondaryLabel: "Voltar para Central de Separação",
      secondaryTo: "/ScreeningAndSeparationHubPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
